import { useEffect, useMemo, useState } from "react";
import { Download, Plus, Save } from "lucide-react";
import { api, exportCsv } from "../../api.js";
import PageTitle from "../../components/common/PageTitle.jsx";
const statuses = [
  "NEW",
  "TRIAL_PENDING",
  "APPROVED",
  "REJECTED",
  "CONTACTED",
  "DEMO_SCHEDULED",
  "DEMO_COMPLETED",
  "TRIAL_ACTIVE",
  "NEGOTIATION",
  "WON",
  "LOST",
];
const blank = {
  companyName: "",
  contactName: "",
  city: "",
  phone: "",
  email: "",
  source: "DIRECT",
  planCode: "",
  departments: [],
  userCount: 1,
  requirements: "",
  customisation: "",
  remarks: "",
  status: "NEW",
  expectedValue: 0,
  nextFollowUpAt: "",
  visitPlannedAt: "",
};
export default function LeadCrmPage({ notify }) {
  const [rows, setRows] = useState([]),
    [filter, setFilter] = useState(""),
    [form, setForm] = useState(blank),
    [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () =>
    api("/saas/leads")
      .then(setRows)
      .catch((e) => notify?.(e.message));
  useEffect(() => {
    load();
  }, []);
  const shown = useMemo(
    () => rows.filter((x) => !filter || x.status === filter),
    [rows, filter],
  );
  async function save(e) {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await api(editing ? `/saas/leads/${editing}` : "/saas/leads", {
        method: editing ? "PUT" : "POST",
        body: JSON.stringify(form),
      });
      setForm(blank);
      setEditing(null);
      load();
      notify?.("Lead saved");
    } catch (x) {
      notify?.(x.message);
    } finally {setSaving(false);}
  }
  function edit(x) {
    setEditing(x._id);
    setForm({
      ...blank,
      ...x,
      nextFollowUpAt: x.nextFollowUpAt?.slice(0, 16) || "",
      visitPlannedAt: x.visitPlannedAt?.slice(0, 16) || "",
    });
  }
  async function activity(x, type) {
    const note = window.prompt(`${type} notes / reason`);
    if (note === null) return;
    const status =
      type === "DEMO"
        ? "DEMO_COMPLETED"
        : type === "CALL"
          ? "CONTACTED"
          : x.status;
    await api(`/saas/leads/${x._id}/activity`, {
      method: "POST",
      body: JSON.stringify({ type, note, status }),
    });
    load();
  }
  async function decide(x, action) {
    const confirmed = window.confirm(
      action === "ACCEPT"
        ? x.requestType === "TRIAL"
          ? "Approve this trial and activate the customer workspace?"
          : "Accept this customer request for follow-up?"
        : "Reject this request? The customer will not receive SaaS access.",
    );
    if (!confirmed) return;

    try {
      const result = await api(`/saas/leads/${x._id}/decision`, {
        method: "PATCH",
        body: JSON.stringify({ action }),
      });
      notify?.(result.message);
      load();
    } catch (error) {
      notify?.(error.message);
    }
  }
  return (
    <section className="classic-page">
      <PageTitle
        title="Lead CRM & Follow-up"
        subtitle="Target companies, calls, visits, demos, requirements, customisation and conversion pipeline"
      />
      <form className="classic-card lead-form" onSubmit={save}>
        <div className="table-toolbar">
          <h3>{editing ? "Update Company Lead" : "Add Target Company"}</h3>
          <button
            type="button"
            onClick={() => exportCsv("ug-saas-leads.xls", shown)}
          >
            <Download /> Excel
          </button>
        </div>
        {editing && <button type="button" onClick={() => {setEditing(null);setForm(blank);}}>Cancel edit</button>}
        <div className="saas-form-grid">
          {[
            ["companyName", "Company Name"],
            ["contactName", "Contact Person"],
            ["city", "City"],
            ["phone", "Phone"],
            ["email", "Email"],
            ["source", "Lead Source"],
            ["planCode", "Interested Plan"],
            ["userCount", "Expected Users", "number"],
            ["expectedValue", "Expected Value ₹", "number"],
            ["nextFollowUpAt", "Next Follow-up", "datetime-local"],
            ["visitPlannedAt", "Visit Plan", "datetime-local"],
          ].map(([k, l, t]) => (
            <label key={k}>
              <span>{l}</span>
              <input
                required={k === "companyName"}
                type={t || "text"}
                value={form[k] || ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    [k]:
                      t === "number" ? Number(e.target.value) : e.target.value,
                  })
                }
              />
            </label>
          ))}
          <label>
            <span>Status</span>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              {statuses.map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </label>
          {[
            ["requirements", "Requirements"],
            ["customisation", "Requested Updates"],
            ["remarks", "Remarks"],
          ].map(([k, l]) => (
            <label className="wide" key={k}>
              <span>{l}</span>
              <textarea
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              />
            </label>
          ))}
        </div>
        <button className="primary" disabled={saving}>
          <Save /> {saving ? "Saving…" : "Save Lead"}
        </button>
      </form>
      <div className="classic-card">
        <div className="table-toolbar">
          <h3>Sales Pipeline</h3>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">All Status</option>
            {statuses.map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </div>
        {!shown.length && <p role="status">No leads match this status.</p>}
        <div className="crm-board">
          {shown.map((x) => (
            <article key={x._id}>
              <header>
                <div>
                  <h3>{x.companyName}</h3>
                  <small>
                    {x.city} · {x.contactName}
                  </small>
                </div>
                <b className={`lead-${x.status.toLowerCase()}`}>
                  {x.status.replaceAll("_", " ")}
                </b>
              </header>
              <p>{x.requirements || "No requirement entered"}</p>
              {x.requestType && x.requestType !== "LEAD" && (
                <p>
                  <b>Request:</b> {x.requestType.replaceAll("_", " ")}
                </p>
              )}
              <dl>
                <div>
                  <dt>Phone</dt>
                  <dd>{x.phone || "—"}</dd>
                </div>
                <div>
                  <dt>Next follow-up</dt>
                  <dd>
                    {x.nextFollowUpAt
                      ? new Date(x.nextFollowUpAt).toLocaleString()
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt>Value</dt>
                  <dd>
                    ₹{Number(x.expectedValue || 0).toLocaleString("en-IN")}
                  </dd>
                </div>
              </dl>
              <footer>
                {["NEW", "TRIAL_PENDING"].includes(x.status) && (
                  <>
                    <button
                      className="primary"
                      onClick={() => decide(x, "ACCEPT")}
                    >
                      Accept
                    </button>
                    <button
                      className="danger"
                      onClick={() => decide(x, "REJECT")}
                    >
                      Reject
                    </button>
                  </>
                )}
                <button disabled={!x.phone} onClick={() => activity(x, "CALL")}>Call</button>
                <button disabled={!x.phone} onClick={() => activity(x, "WHATSAPP")}>
                  WhatsApp
                </button>
                <button disabled={!x.email} onClick={() => activity(x, "EMAIL")}>Email</button>
                <button onClick={() => activity(x, "VISIT")}>Visit</button>
                <button onClick={() => activity(x, "DEMO")}>Demo</button>
                <button onClick={() => edit(x)}>Edit</button>
              </footer>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
